import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { formatCurrency } from "../../../utils/format";

const sanitizeNumber = (value) => {
  if (value === undefined || value === null) return 0;
  const sanitized = String(value).replace(/[^0-9.-]+/g, "");
  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const calcMonthlyPayment = (principal, annualRate, payments) => {
  if (payments === 0) return 0;
  const monthlyRate = annualRate / 1200;
  if (monthlyRate === 0) return principal / payments;
  const factor = Math.pow(1 + monthlyRate, payments);
  return (principal * monthlyRate * factor) / (factor - 1);
};

const generateSchedule = ({ principal, annualRate, termYears, dueYears }) => {
  const sanitizedPrincipal = sanitizeNumber(principal);
  const sanitizedRate = sanitizeNumber(annualRate);
  const dueYearsNum = sanitizeNumber(dueYears) || sanitizeNumber(termYears);
  const durationMonths = Math.round(dueYearsNum * 12);

  if (sanitizedPrincipal <= 0 || sanitizedRate < 0 || durationMonths <= 0) {
    return [];
  }

  const monthlyPayment = calcMonthlyPayment(sanitizedPrincipal, sanitizedRate, durationMonths);
  const monthlyRate = sanitizedRate / 1200;
  const schedule = [];
  let balance = sanitizedPrincipal;

  for (let i = 1; i <= durationMonths; i++) {
    const interestPayment = Number((balance * monthlyRate).toFixed(2));
    const principalPayment = Number((monthlyPayment - interestPayment).toFixed(2));
    balance = Number((balance - principalPayment).toFixed(2));
    schedule.push({
      month: i,
      payment: monthlyPayment.toFixed(2),
      interest: interestPayment.toFixed(2),
      principal: principalPayment.toFixed(2),
      balance: balance < 0 ? "0.00" : balance.toFixed(2),
      balanceNum: balance,
      interestNum: interestPayment,
      principalNum: principalPayment,
    });
  }
  return schedule;
};

const ROWS_PER_PAGE = 24;
const CHART_SAMPLE = 6; // use every Nth month for chart (e.g. 30yr → 60 points)

function AmortizationChart({ schedule, formatCurrencyFn }) {
  const chartData = useMemo(() => {
    if (!schedule.length) return [];
    return schedule
      .filter((_, i) => (i + 1) % CHART_SAMPLE === 0 || i === 0 || i === schedule.length - 1)
      .map((r) => ({
        month: r.month,
        year: (r.month / 12).toFixed(1),
        balance: r.balanceNum,
        principal: r.principalNum,
        interest: r.interestNum,
        balanceFmt: formatCurrencyFn(r.balance),
      }))
      .sort((a, b) => a.month - b.month);
  }, [schedule, formatCurrencyFn]);

  if (!chartData.length) {
    return (
      <div className="amort-chart-empty">
        <p>No data to display. Enter valid loan details.</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="amort-chart-tooltip">
        <div>Month {d.month}</div>
        <div>Balance: {formatCurrencyFn(d.balance)}</div>
        <div>Principal: {formatCurrencyFn(d.principal)}</div>
        <div>Interest: {formatCurrencyFn(d.interest)}</div>
      </div>
    );
  };

  return (
    <div className="amort-chart-wrap">
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart data={chartData} margin={{ top: 12, right: 12, left: 12, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="month"
            type="number"
            tickFormatter={(m) => (m >= 12 ? `${Math.round(m / 12)}y` : m)}
            stroke="#6b7280"
            fontSize={12}
          />
          <YAxis
            tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)}
            stroke="#6b7280"
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Area
            type="monotone"
            dataKey="balance"
            name="Remaining balance"
            stroke="var(--color-primary, #8a9c6a)"
            fill="var(--color-primary, #8a9c6a)"
            fillOpacity={0.3}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function AmortizationScheduleModal({
  type,
  onClose,
  buyerFormData,
  sellerFormData,  
  handleEmail,
  handlePrint,
}) {
  const [principalBorrowed, setPrincipalBorrowed] = useState("");
  const [annualInterestRate, setAnnualInterestRate] = useState("");
  const [termOfLoanInYears, setTermOfLoanInYears] = useState("");
  const [dueYear, setDueYear] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [displayType, setDisplayType] = useState("list");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const optionsRef = useRef(null);

  const loadFormData = useCallback(() => {
    const source = type === "buyer" ? buyerFormData : sellerFormData;
    setPrincipalBorrowed(source?.loanInfoMeta?.amount ?? "");
    setAnnualInterestRate(source?.loanInfoMeta?.interestRate1 ?? "");
    setTermOfLoanInYears(source?.loanInfoMeta?.termsInYears1 ?? "");
    setDueYear(source?.loanInfoMeta?.addDueYear1 ?? "");
  }, [buyerFormData, sellerFormData, type]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  useEffect(() => {
    const next = generateSchedule({
      principal: principalBorrowed,
      annualRate: annualInterestRate,
      termYears: termOfLoanInYears,
      dueYears: dueYear,
    });
    setSchedule(next);
    setPage(1);
  }, [principalBorrowed, annualInterestRate, termOfLoanInYears, dueYear]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) {
        setOptionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displaySchedule = useMemo(() => schedule, [schedule]);
  const totalPages = Math.max(1, Math.ceil(displaySchedule.length / ROWS_PER_PAGE));
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return displaySchedule.slice(start, start + ROWS_PER_PAGE);
  }, [displaySchedule, page]);

  function getPaginationRange(totalPages, currentPage) {
    if (totalPages <= 3) return [...Array(totalPages)].map((_, i) => i + 1);
    if (currentPage === 1) return [1, 2, 3];
    if (currentPage === totalPages) return [totalPages - 2, totalPages - 1, totalPages];
    return [currentPage - 1, currentPage, currentPage + 1];
  }

  const setDisplayTypeFn = useCallback((viewType) => {
    setDisplayType(viewType);
  }, []);



  return (
    <div className="modelspace application-fe model-select space-fields amort-modal">
      <div className="amort-summary myadmin-advancesearch">
        <table className="amortization-schedule-table amort-summary-table">
          <tbody>
            <tr>
              <th><b>Principal Borrowed:</b></th>
              <td>$ {formatCurrency(principalBorrowed)}</td>
              <th className="blnk-space">&nbsp;</th>
              <th><b>Annual Interest Rate:</b></th>
              <td>{annualInterestRate || "0.00"}%</td>
            </tr>
          
            <tr>
              
              <th><b>Term of Loan (Years):</b></th>
              <td>{termOfLoanInYears || "0"}</td>
              <th className="blnk-space">&nbsp;</th>
               <th><b>Due Year:</b></th>
              <td>{dueYear || "0"}</td>
            </tr>
           
          </tbody>
        </table>
      </div>

      <div className="amort-toolbar">
        <div className="amort-view-toggle">
          <button
            type="button"
            className={`amort-view-btn ${displayType === "chart" ? "active-view" : ""}`}
            onClick={() => setDisplayTypeFn("chart")}
          >
            <span className="amort-view-icon">📈</span> Chart
          </button>
          <button
            type="button"
            className={`amort-view-btn ${displayType === "list" ? "active-view" : ""}`}
            onClick={() => setDisplayTypeFn("list")}
          >
            <span className="amort-view-icon">📋</span> List
          </button>
        </div>

        <div className="amort-options-wrap" ref={optionsRef}>
          <button
            type="button"
            className="btn dropdown-toggle amort-options-btn"
            onClick={() => setOptionsOpen((o) => !o)}
            aria-expanded={optionsOpen}
            aria-haspopup="true"
          >
            Options ▾
          </button>
          {optionsOpen && (
            <div className="dropdown-menu amort-options-menu" role="menu">
              <button type="button" className="dropdown-item icon-print" onClick={handlePrint} role="menuitem">
                Print
              </button>
              <button type="button" className="dropdown-item icon-email" onClick={handleEmail} role="menuitem">
                Email
              </button>
            </div>
          )}
        </div>
      </div>

      {displayType === "list" && (
        <div className="tbl-type-a tbl-authroziation">
          <div className="open-tbl-section">
            <div className="set-tbl-height">
                <div className="table-overflow amort-table-wrap">
          <table className="amortization-schedule-table" cellPadding="0" cellSpacing="0">
            <thead>
              <tr>
                <th className="txt-center-imp">Month</th>
                <th className="txt-right-imp">Monthly Payment</th>
                <th className="txt-right-imp">Interest Portion</th>
                <th className="txt-right-imp">Principal Portion</th>
                <th className="txt-right-imp">Principal Balance</th>
              </tr>
            </thead>
            <tbody>
              {displaySchedule.length === 0 ? (
                <tr>
                  <td colSpan="5" className="amort-empty">
                    No amortization data available. Enter valid loan details.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => (
                  <tr key={row.month}>
                    <td className="txt-center-imp">{row.month}</td>
                    <td className="txt-right-imp">$ {formatCurrency(row.payment)}</td>
                    <td className="txt-right-imp">$ {formatCurrency(row.interest)}</td>
                    <td className="txt-right-imp">$ {formatCurrency(row.principal)}</td>
                    <td className="txt-right-imp">$ {formatCurrency(row.balance)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {displaySchedule.length > ROWS_PER_PAGE && (
            <div className="open-pagination pagination-desktop no-print">
              <nav className="pagination">
                <button disabled={page === 1}
                  className={`page prev ${page === 1 ? "is-disabled" : ""}`}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>

                {getPaginationRange(totalPages, page).map((pageNum, idx) => (
                  <button
                    key={idx}
                    className={`page${page === pageNum ? " is-active" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}

                <button disabled={page === totalPages}
                  className={`page next ${page === totalPages ? "is-disabled" : ""}`}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
            </div>
          </div>
        </div>
        
      )}

      {displayType === "chart" && (
        <div className="amort-chart-container no-print">
          <AmortizationChart schedule={schedule} formatCurrencyFn={formatCurrency} />
        </div>
      )}

      <div className="frm-btn popup-2-btn no-print amor-close">
        <button type="button" className="submit-btn btn-bg inpt-btn btn-discard" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
